import React, { useState } from 'react';
import {
  ClipboardDocumentIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckIcon,
  ServerIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { SFTPCredentials } from '../../types/uploads.types';

interface SftpCredentialsSectionProps {
  credentials: SFTPCredentials;
  onResetPassword: () => void;
  compact?: boolean;
}

const SftpCredentialsSection: React.FC<SftpCredentialsSectionProps> = ({
  credentials,
  onResetPassword,
  compact = false,
}) => {
  const { t } = useTranslation('uploads');
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const CopyButton: React.FC<{ text: string; field: string }> = ({ text, field }) => (
    <button
      onClick={() => copyToClipboard(text, field)}
      className="ml-2 p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
      title={t('credentials.copy')}
    >
      {copiedField === field ? (
        <CheckIcon className="h-4 w-4 text-green-500" />
      ) : (
        <ClipboardDocumentIcon className="h-4 w-4" />
      )}
    </button>
  );

  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">{t('credentials.server')}</span>
          <div className="flex items-center">
            <code className="text-sm font-mono text-gray-900">{credentials.host}:{credentials.port}</code>
            <CopyButton text={`${credentials.host}:${credentials.port}`} field="host" />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">{t('credentials.username')}</span>
          <div className="flex items-center">
            <code className="text-sm font-mono text-gray-900">{credentials.username}</code>
            <CopyButton text={credentials.username} field="username" />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">{t('credentials.password')}</span>
          <div className="flex items-center">
            <code className="text-sm font-mono text-gray-900">
              {showPassword ? credentials.password : '••••••••••••'}
            </code>
            <button
              onClick={() => setShowPassword(!showPassword)}
              className="ml-2 p-1 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? (
                <EyeSlashIcon className="h-4 w-4" />
              ) : (
                <EyeIcon className="h-4 w-4" />
              )}
            </button>
            <CopyButton text={credentials.password} field="password" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <div className="flex items-center">
          <ServerIcon className="h-6 w-6 text-indigo-500 mr-3" />
          <h3 className="text-lg font-medium text-gray-900">{t('credentials.title')}</h3>
        </div>
        <p className="mt-1 text-sm text-gray-500">{t('credentials.description')}</p>
      </div>
      <div className="px-4 py-5 sm:p-6">
        <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-gray-500">{t('credentials.server')}</dt>
            <dd className="mt-1 flex items-center">
              <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                {credentials.host}
              </code>
              <CopyButton text={credentials.host} field="host" />
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t('credentials.port')}</dt>
            <dd className="mt-1 flex items-center">
              <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                {credentials.port}
              </code>
              <CopyButton text={String(credentials.port)} field="port" />
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t('credentials.username')}</dt>
            <dd className="mt-1 flex items-center">
              <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                {credentials.username}
              </code>
              <CopyButton text={credentials.username} field="username" />
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t('credentials.password')}</dt>
            <dd className="mt-1 flex items-center">
              <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                {showPassword ? credentials.password : '••••••••••••••••'}
              </code>
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="ml-2 p-1 text-gray-400 hover:text-gray-600"
                title={showPassword ? t('credentials.hidePassword') : t('credentials.showPassword')}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                )}
              </button>
              <CopyButton text={credentials.password} field="password" />
            </dd>
          </div>
        </dl>

        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {credentials.provisioned_at && (
              <span>
                {t('credentials.provisionedAt')}: {new Date(credentials.provisioned_at).toLocaleDateString()}
              </span>
            )}
          </div>
          <button
            onClick={onResetPassword}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {t('credentials.resetPassword')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SftpCredentialsSection;
